package com.xtream.ui.tv.presentation.widget


import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.DrawerState
import androidx.compose.material3.DrawerValue
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.rememberDrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.FocusState
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.zIndex
import kotlinx.coroutines.launch

/**
 * Navigation drawers provide ergonomic access to destinations in an app. Modal navigation drawers
 * are good for infrequent, but more focused, switching to different destinations.
 *
 * It displays content associated with the closed state when the drawer is not in focus and displays
 * content associated with the open state when the drawer or its contents are focused on. Modal
 * navigation drawers are elevated above most of the app’s UI and don’t affect the screen’s layout
 * grid.
 *
 * Example:
 *
 * @sample androidx.tv.material3.samples.SampleModalNavigationDrawerWithSolidScrim
 * @sample androidx.tv.material3.samples.SampleModalNavigationDrawerWithGradientScrim
 * @param drawerContent Content that needs to be displayed on the drawer based on whether the drawer
 *   is [DrawerValue.Open] or [DrawerValue.Closed]. Drawer-entries can be animated when the drawer
 *   moves from Closed to Open state and vice-versa. For, e.g., the entry could show only an icon in
 *   the Closed state and slide in text to form (icon + text) when in the Open state.
 *
 * To limit the width of the drawer in the open or closed state, wrap the content in a box with the
 * required width.
 *
 * @param modifier the [Modifier] to be applied to this drawer
 * @param drawerState state of the drawer
 * @param scrimBrush brush to paint the scrim that obscures content when the drawer is open
 * @param content content of the rest of the UI. The content extends to the edge of the container
 *   under the modal navigation drawer. Focusable content that is not part of the background must
 *   have start-padding sufficient to prevent it from being drawn under the drawer in the Closed
 *   state.
 */
@Composable
fun ModalNavigationDrawer(
    drawerContent: @Composable NavigationDrawerScope.(DrawerValue) -> Unit,
    modifier: Modifier = Modifier,
    drawerState: DrawerState = rememberDrawerState(DrawerValue.Closed),
    scrimBrush: Brush = SolidColor(MaterialTheme.colorScheme.scrim.copy(alpha = 0.5f)),
    content: @Composable () -> Unit,
) {
    val localDensity = LocalDensity.current
    val closedDrawerWidth: MutableState<Dp?> = remember { mutableStateOf(null) }
    val internalDrawerModifier =
        Modifier.zIndex(Float.MAX_VALUE).onSizeChanged {
            if (closedDrawerWidth.value == null && drawerState.currentValue == DrawerValue.Closed) {
                with(localDensity) { closedDrawerWidth.value = it.width.toDp() }
            }
        }

    Box(modifier = modifier) {
        DrawerSheet(
            modifier = internalDrawerModifier.align(Alignment.CenterStart),
            drawerState = drawerState,
            sizeAnimationFinishedListener = { _, targetSize ->
                if (drawerState.currentValue == DrawerValue.Closed) {
                    with(localDensity) { closedDrawerWidth.value = targetSize.width.toDp() }
                }
            },
            content = drawerContent,
        )

        content()

        if (drawerState.currentValue == DrawerValue.Open) {
            // Scrim
            Canvas(Modifier.fillMaxSize()) { drawRect(scrimBrush) }
        }
    }
}

/**
 * Navigation drawers provide ergonomic access to destinations in an app. They’re often next to app
 * content and affect the screen’s layout grid. Standard navigation drawers are good for frequent
 * switching to different destinations.
 *
 * It displays content associated with the closed state when the drawer is not in focus and displays
 * content associated with the open state when the drawer or its contents are focused on. The drawer
 * is at the same level as the app's UI an reduces the screen size available to the remaining
 * content.
 *
 * Example:
 *
 * @sample androidx.tv.material3.samples.SampleNavigationDrawer
 * @param drawerContent Content that needs to be displayed on the drawer based on whether the drawer
 *   is [DrawerValue.Open] or [DrawerValue.Closed]. Drawer-entries can be animated when the drawer
 *   moves from Closed to Open state and vice-versa. For, e.g., the entry could show only an icon in
 *   the Closed state and slide in text to form (icon + text) when in the Open state.
 *
 * To limit the width of the drawer in the open or closed state, wrap the content in a box with the
 * required width.
 *
 * @param modifier the [Modifier] to be applied to this drawer
 * @param drawerState state of the drawer
 * @param content content of the rest of the UI
 */
@Composable
fun NavigationDrawer(
    drawerContent: @Composable NavigationDrawerScope.(DrawerValue) -> Unit,
    modifier: Modifier = Modifier,
    drawerState: DrawerState = rememberDrawerState(DrawerValue.Closed),
    content: @Composable () -> Unit,
) {
    Row(modifier = modifier) {
        DrawerSheet(drawerState = drawerState, content = drawerContent)
        content()
    }
}




@Composable
private fun DrawerSheet(
    modifier: Modifier = Modifier,
    drawerState: DrawerState = remember { DrawerState(  DrawerValue.Closed ) },
    sizeAnimationFinishedListener: ((initialValue: IntSize, targetValue: IntSize) -> Unit)? = null,
    content: @Composable NavigationDrawerScope.(DrawerValue) -> Unit,
) {
    // indicates that the drawer has been set to its initial state and has grabbed focus if
    // necessary. Controls whether focus is used to decide the state of the drawer going forward.
    var initializationComplete: Boolean by remember { mutableStateOf(false) }
    var focusState by remember { mutableStateOf<FocusState?>(null) }
    val focusRequester = remember { FocusRequester() }
    val scope= rememberCoroutineScope()
    LaunchedEffect(key1 = drawerState.currentValue) {
        if (drawerState.currentValue == DrawerValue.Open && focusState?.hasFocus == false) {
            // used to grab focus if the drawer state is set to Open on start.
            focusRequester.requestFocus()
        }
        initializationComplete = true
    }

    val internalModifier =
        Modifier.focusRequester(focusRequester)
            .animateContentSize(finishedListener = sizeAnimationFinishedListener)
            .fillMaxHeight()
            // adding passed-in modifier here to ensure animateContentSize is called before other
            // size based modifiers.
            .then(modifier)
            .onFocusChanged {
                focusState = it

                if (initializationComplete) {
                    scope.launch {
                        if (it.hasFocus)
                            drawerState.open()
                        else drawerState.close()
                    }
                }
            }
            .focusGroup()

    Box(modifier = internalModifier) {
        NavigationDrawerScopeImpl(drawerState.currentValue == DrawerValue.Open).apply {
            content(drawerState.currentValue)
        }
    }
}